<template>
  <div class="vote-rules-page">
    <!-- 浮动光斑 -->
    <div class="float-spot spot-1"></div>
    <div class="float-spot spot-2"></div>
    <div class="float-spot spot-3"></div>
    <div class="float-spot spot-4"></div>

    <!-- 顶部装饰 -->
    <div class="top-banner">
      <div class="banner-bg"></div>
      <!-- 语言切换 -->
      <div class="lang-switch">
        <span class="lang-slider" :class="currentLang === 'id' ? 'slide-id' : 'slide-zh'"></span>
        <a class="lang-opt" :class="{ active: currentLang === 'id' }" href="?lang=id" @click.prevent="switchLang('id')" @touchstart.prevent="switchLang('id')">ID</a>
        <a class="lang-opt" :class="{ active: currentLang === 'zh' }" href="?lang=zh" @click.prevent="switchLang('zh')" @touchstart.prevent="switchLang('zh')">中文</a>
      </div>
      <div class="banner-content">
        <p class="banner-gloat">{{ currentLang === 'zh' ? '杰出之星你选择，千元大奖等你拿' : 'Pilih Bintang Unggulan, Menangkan Hadiah Besar Seribu Yuan!!' }}</p>
        <h1 class="banner-title">{{ $t('rules.title') }}</h1>
        <p class="banner-sub">{{ currentLang === 'zh' ? '您的一票至关重要，热切期待您的参与！' : 'Suara Anda sangat penting, kami sangat menantikan partisipasi Anda!' }}</p>
        <!-- 倒计时 -->
        <div class="countdown-bar" :class="'cd-' + (statusInfo && statusInfo.status)" v-if="statusInfo">
          <span class="countdown-label">{{ statusInfo.label }}</span>
          <span class="countdown-time" v-if="statusInfo.countdown">{{ statusInfo.countdown }}</span>
          <span class="countdown-range">{{ statusInfo.timeRange }}</span>
        </div>
      </div>
    </div>

    <!-- 规则卡片列表 -->
    <div class="rules-list">
      <div class="rule-card" v-for="(rule, index) in rules" :key="index">
        <div class="rule-number">{{ index + 1 }}</div>
        <div class="rule-body">
          <p class="rule-text-zh" v-if="currentLang === 'zh'">{{ rule.zh }}</p>
          <p class="rule-text-id" v-if="currentLang === 'id'">{{ rule.id }}</p>
        </div>
      </div>
    </div>

    <!-- 复选框 -->
    <div class="agree-box">
      <label class="agree-label" @click="agreed = !agreed">
        <span class="checkbox" :class="{ checked: agreed }">
          <svg v-if="agreed" viewBox="0 0 24 24" width="16" height="16" fill="#fff">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
          </svg>
        </span>
        <span class="agree-text">{{ $t('rules.agree') }}</span>
      </label>
    </div>

    <!-- 开始按钮 -->
    <div class="btn-area">
      <button class="start-btn" :class="{ disabled: !agreed }" :disabled="!agreed" @click="goVote">
        <span class="btn-shine"></span>
        <span class="btn-text">{{ voteBtnText }}</span>
        <span class="btn-ring"></span>
      </button>
    </div>

    <!-- 解释权声明 -->
    <div class="disclaimer-text">{{ currentLang === 'zh' ? '本次抽奖唯一解释权归IPIP园区所有' : 'Hak interpretasi tunggal undian ini dimiliki oleh IPIP Park' }}</div>

    <!-- Toast 提示 -->
    <transition name="toast-fade">
      <div class="toast" v-if="showToast">{{ toastMsg }}</div>
    </transition>
  </div>
</template>

<script>
import { t, getLang, setLang } from '../utils/i18n'
import { canVote, getStatusInfo } from '../utils/voteTime'
import { getAuthCode, isInDingTalk } from '../utils/dingtalk'
import { ssoLogin, checkAuth } from '../api/auth'
import { logAccess, logSSO, logError } from '../utils/serverLog'

export default {
  name: 'VoteRules',
  data () {
    return {
      agreed: false,
      currentLang: getLang(),
      rules: [],
      canVoteNow: true,
      statusInfo: null,
      showToast: false,
      toastMsg: '',
      toastTimer: null,
      countdownTimer: null,
      authPromise: Promise.resolve()
    }
  },
  computed: {
    voteBtnText () {
      const isZh = this.currentLang === 'zh'
      return isZh ? '开始投票' : 'Mulai'
    }
  },
  methods: {
    $t (key) { return t(key) },
    initRules () {
      const isZh = this.currentLang === 'zh'
      this.rules = [
        {
          zh: '30位候选人，5家公司，一次选满10票',
          id: '30 kandidat, 5 perusahaan, pilih 10 sekaligus'
        },
        {
          zh: '配额：IPIP 3 · 南建 1 · 华星 3 · KNI 2 · HBB 1',
          id: 'Kuota tetap per perusahaan: IPIP 3 · NJ 1 · HX 3 · KNI 2 · HBB 1'
        },
        {
          zh: '一个钉钉账号仅投一次，提交后不可修改',
          id: 'Satu akun satu kali, terkunci permanen'
        },
        {
          zh: '系统实时监测并拦截异常刷票，提交选票后可查看候选人票数',
          id: 'Sistem memantau dan mencegah kecurangan voting secara real-time, jumlah suara kandidat dapat dilihat setelah mengirimkan suara'
        }
      ]
    },
    // 进入页面时静默重新认证，确保后续流程 session 有效
    // 返回 promise，供 goVote 等待
    reAuthOnEntry () {
      return new Promise((resolve) => {
        if (!isInDingTalk()) {
          logAccess('VoteRules重新认证跳过，非钉钉环境')
          resolve(false)
          return
        }
        const corpId = this.$store.state.dingtalkCorpId || ''
        logSSO('VoteRules开始重新认证', { corpId })
        getAuthCode(corpId).then(code => {
          logSSO('VoteRules重新认证获取code成功', { corpId })
          return ssoLogin(code)
        }).then(resp => {
          logSSO('VoteRules重新认证ssoLogin返回', { code: resp.data.code, message: resp.data.message })
          if (resp.data && resp.data.code === 200) {
            const data = resp.data.result
            const newUserName = data.userName || data.name || ('user_' + data.mobile)
            this.$store.commit('login', {
              userName: newUserName,
              loginType: 'dingtalk',
              jobNumber: data.jobNumber || ''
            })
            logSSO('VoteRules重新认证成功', { userName: newUserName })
            resolve(true)
          } else {
            logError('VoteRules重新认证失败', { code: resp.data.code, message: resp.data.message })
            resolve(false)
          }
        }).catch(e => {
          logError('VoteRules重新认证异常', { error: e.message || JSON.stringify(e) })
          resolve(false)
        })
      })
    },
    switchLang (lang) {
      this.currentLang = lang
      setLang(lang)
      this.initRules()
      this.refreshStatus()
      this.$forceUpdate()
    },
    showToastMsg (msg) {
      this.toastMsg = msg
      this.showToast = true
      clearTimeout(this.toastTimer)
      this.toastTimer = setTimeout(() => {
        this.showToast = false
      }, 2000)
    },
    goVote () {
      if (!this.agreed) return

      // 直接跳转投票页，VotePage 会处理认证/已投票/已结束的展示
      window.localStorage.setItem('has_agreed_rules', 'true')
      this.$router.push('/vote')
    },
    refreshStatus () {
      this.canVoteNow = canVote()
      this.statusInfo = getStatusInfo(this.currentLang)
    }
  },
  created () {
    document.title = t('rules.title')
    logAccess('VoteRules页面created', { href: window.location.href, inDingTalk: isInDingTalk() })
    this.initRules()
    this.refreshStatus()

    // 进入投票规则页面时，先检查 session 有效性，如果过期则静默重新认证
    if (isInDingTalk()) {
      logAccess('VoteRules检查session有效性')
      this.authPromise = checkAuth().then(authResp => {
        logAccess('VoteRules checkAuth返回', { code: authResp.data.code })
        if (!(authResp.data && authResp.data.code === 200)) {
          logAccess('VoteRules session失效，开始重新认证')
          return this.reAuthOnEntry()
        }
        logAccess('VoteRules session有效')
        return Promise.resolve()
      }).catch(() => {
        logAccess('VoteRules checkAuth异常，开始重新认证')
        return this.reAuthOnEntry()
      })
    } else {
      logAccess('VoteRules非钉钉环境，跳过认证检查')
      this.authPromise = Promise.resolve()
    }

    // 每秒刷新倒计时
    this.countdownTimer = setInterval(() => {
      this.refreshStatus()
    }, 1000)
  },
  beforeDestroy () {
    clearInterval(this.countdownTimer)
    clearTimeout(this.toastTimer)
  }
}
</script>

<style scoped>
.vote-rules-page {
  background: linear-gradient(135deg, #dce8f5 0%, #e8f0fe 20%, #f0e8ff 45%, #fce4ec 70%, #fff3e0 100%);
  position: relative;
  width: 100%;
}

/* ===== 浮动光斑 ===== */
.float-spot {
  position: fixed;
  border-radius: 50%;
  pointer-events: none;
  filter: blur(50px);
  z-index: 0;
}
.spot-1 {
  width: 280px; height: 280px;
  background: radial-gradient(circle, rgba(74,108,247,0.12), transparent);
  top: -80px; right: -60px;
  animation: spotFloat1 12s ease-in-out infinite;
}
.spot-2 {
  width: 240px; height: 240px;
  background: radial-gradient(circle, rgba(22,119,255,0.08), transparent);
  bottom: 25%; left: -80px;
  animation: spotFloat2 10s ease-in-out infinite;
}
.spot-3 {
  width: 180px; height: 180px;
  background: radial-gradient(circle, rgba(250,112,154,0.08), transparent);
  top: 45%; right: 8%;
  animation: spotFloat3 14s ease-in-out infinite;
}
.spot-4 {
  width: 140px; height: 140px;
  background: radial-gradient(circle, rgba(64,150,255,0.06), transparent);
  bottom: 10%; right: 30%;
  animation: spotFloat4 9s ease-in-out infinite;
}

@keyframes spotFloat1 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(-30px, 40px) scale(1.1); }
}
@keyframes spotFloat2 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(40px, -30px) scale(1.15); }
}
@keyframes spotFloat3 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(-20px, 30px) scale(1.1); }
}
@keyframes spotFloat4 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(20px, -20px) scale(1.2); }
}

/* ===== 顶部 ===== */
.top-banner {
  position: relative;
  overflow: hidden;
  z-index: 1;
}
.banner-bg {
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 100%;
  background: linear-gradient(135deg, #4a6cf7 0%, #667eea 35%, #764ba2 65%, #f093fb 100%);
  border-radius: 0 0 36px 36px;
}
.banner-content {
  position: relative;
  z-index: 1;
  text-align: center;
  padding: 30px 16px 12px;
}
.banner-gloat {
  font-size: 16px;
  font-weight: 800;
  color: #ffd700;
  margin: 0 0 4px;
  text-shadow: 0 1px 4px rgba(0,0,0,0.3);
  letter-spacing: 1px;
}
.banner-title {
  font-size: 18px;
  font-weight: 800;
  color: #fff;
  margin: 0;
  letter-spacing: 2px;
}
.banner-sub {
  font-size: 14px;
  color: rgba(255,255,255,0.95);
  margin: 5px 0 0;
  letter-spacing: 1px;
  font-weight: 600;
  line-height: 1.4;
}

/* 倒计时 */
.countdown-bar {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 4px; margin-top: 10px;
  border-radius: 20px; padding: 8px 16px;
  backdrop-filter: blur(4px);
}
/* 进行中 — 暖橙金 */
.countdown-bar.cd-during {
  background: linear-gradient(135deg, rgba(255,183,77,0.35), rgba(255,152,0,0.3));
  border: 1px solid rgba(255,183,77,0.4);
}
/* 未开始 — 清新蓝 */
.countdown-bar.cd-before {
  background: linear-gradient(135deg, rgba(64,150,255,0.35), rgba(100,180,255,0.3));
  border: 1px solid rgba(64,150,255,0.4);
}
/* 已结束 — 低调灰 */
.countdown-bar.cd-after {
  background: linear-gradient(135deg, rgba(180,180,180,0.3), rgba(150,150,150,0.25));
  border: 1px solid rgba(180,180,180,0.35);
}
.countdown-label {
  font-size: 11px; color: #ffe082; font-weight: 600;
}
.countdown-time {
  font-size: 14px; color: #fff; font-weight: 800;
  letter-spacing: 1px;
  font-variant-numeric: tabular-nums;
}
.countdown-range {
  font-size: 11px; color: rgba(255,255,255,0.7);
}

/* ===== 规则卡片 ===== */
.rules-list {
  display: flex;
  flex-direction: column;
  padding: 24px 12px;
  position: relative;
  z-index: 1;
}
.rule-card {
  background: #fff;
  border-radius: 14px;
  padding: 10px 12px 10px 48px;
  margin-bottom: 20px;
  position: relative;
  min-height: 44px;
  display: flex;
  align-items: center;
  box-shadow: 0 4px 16px rgba(74,108,247,0.08), 0 1px 4px rgba(0,0,0,0.04);
  transition: all 0.25s ease;
  border-left: 3px solid transparent;
}
.rule-card:active {
  transform: translateY(-1px) scale(1.01);
  box-shadow: 0 6px 20px rgba(74,108,247,0.15);
  border-left-color: #4a6cf7;
}
.rule-number {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  background: linear-gradient(135deg, #4a6cf7, #667eea, #f093fb);
  color: #fff;
  font-size: 12px;
  font-weight: 800;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(74,108,247,0.25);
}
.rule-body {
  flex: 1;
  text-align: left;
}
.rule-text-zh {
  font-size: 12px;
  color: #1a1a2e;
  margin: 0;
  font-weight: 600;
  line-height: 1.3;
}
.rule-text-id {
  font-size: 12px;
  color: #1a1a2e;
  margin: 0;
  font-weight: 600;
  line-height: 1.3;
}



/* ===== 复选框 ===== */
.agree-box {
  position: relative;
  z-index: 1;
  padding: 0 20px 6px;
}
.agree-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}
.checkbox {
  width: 20px;
  height: 20px;
  border: 2px solid #ccc;
  border-radius: 5px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}
.checkbox.checked {
  background: linear-gradient(135deg, #4a6cf7, #667eea, #f093fb);
  border-color: transparent;
  box-shadow: 0 2px 8px rgba(74,108,247,0.3);
}
.agree-text {
  font-size: 12px;
  color: #666;
  line-height: 1.2;
}

/* ===== 按钮 ===== */
.btn-area {
  position: relative;
  z-index: 1;
  padding: 0 20px 4px;
}
.start-btn {
  position: relative;
  width: 100%;
  height: 50px;
  border: none;
  border-radius: 25px;
  font-size: 16px;
  font-weight: 800;
  letter-spacing: 4px;
  cursor: pointer;
  -webkit-appearance: none;
  outline: none;
  overflow: hidden;
  transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
  background: transparent;
  color: #fff;
  z-index: 1;
}

/* ---- 禁用状态 ---- */
.start-btn.disabled {
  background: #d9d9d9;
  color: #999;
  cursor: not-allowed;
  pointer-events: none;
  box-shadow: none;
  animation: none;
}
.start-btn.disabled .btn-shine,
.start-btn.disabled .btn-ring {
  display: none;
}

/* ---- 可用状态 ---- */
.start-btn:not(.disabled) {
  background: linear-gradient(135deg, #4a6cf7 0%, #667eea 30%, #764ba2 60%, #f093fb 100%);
  background-size: 300% 300%;
  animation: gradientFlow 3s ease infinite, boxPulse 2s ease-in-out infinite;
}

/* 扫光 */
.btn-shine {
  position: absolute;
  top: 0;
  left: -100%;
  width: 60%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent);
  transform: skewX(-20deg);
  pointer-events: none;
  z-index: 2;
}
.start-btn:not(.disabled) .btn-shine {
  animation: shineSweep 2.5s ease-in-out infinite;
}

/* 按钮文字 */
.btn-text {
  position: relative;
  z-index: 3;
  display: inline-block;
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.start-btn:not(.disabled):hover .btn-text {
  transform: scale(1.04);
}

/* 外圈脉冲环 */
.btn-ring {
  position: absolute;
  top: -6px;
  left: -6px;
  right: -6px;
  bottom: -6px;
  border-radius: 32px;
  border: 2px solid rgba(74,108,247,0.15);
  pointer-events: none;
  z-index: 0;
}
.start-btn:not(.disabled) .btn-ring {
  animation: ringPulse 2s ease-in-out infinite;
}

/* 点击反馈 */
.start-btn:not(.disabled):active {
  transform: scale(0.93);
}
.start-btn:not(.disabled):active .btn-text {
  transform: scale(0.96);
}

/* ===== 动画关键帧 ===== */
@keyframes gradientFlow {
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}
@keyframes boxPulse {
  0%, 100% {
    box-shadow:
      0 4px 20px rgba(74,108,247,0.35),
      0 0 0 0 rgba(74,108,247,0.12);
  }
  50% {
    box-shadow:
      0 8px 36px rgba(74,108,247,0.55),
      0 0 0 14px rgba(74,108,247,0.04);
  }
}
@keyframes shineSweep {
  0%   { left: -60%; }
  50%  { left: 120%; }
  100% { left: -60%; }
}
@keyframes ringPulse {
  0%, 100% {
    opacity: 0.5;
    transform: scale(1);
    border-color: rgba(74,108,247,0.2);
  }
  50% {
    opacity: 1;
    transform: scale(1.08);
    border-color: rgba(74,108,247,0.35);
  }
}

/* ===== 语言切换 ===== */
.lang-switch {
  position: absolute;
  top: 8px;
  right: 12px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  background: rgba(255,255,255,0.15);
  border-radius: 14px;
  padding: 2px;
  cursor: pointer;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
  transform: scale(0.85);
  transform-origin: right top;
}
.lang-opt {
  position: relative;
  z-index: 1;
  padding: 4px 12px;
  min-width: 30px;
  text-align: center;
  color: rgba(255,255,255,0.7);
  font-size: 11px;
  transition: color 0.2s;
  text-decoration: none;
  -webkit-tap-highlight-color: transparent;
  outline: none;
  -webkit-appearance: none;
  background: transparent;
  border: none;
}
.lang-opt.active {
  color: #fff;
  font-weight: 700;
}
.lang-slider {
  position: absolute;
  top: 2px;
  left: 2px;
  width: calc(50% - 2px);
  height: calc(100% - 4px);
  background: #daa520;
  border-radius: 12px;
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 0;
}
.lang-slider.slide-zh {
  transform: translateX(100%);
}

/* ===== Toast ===== */
.toast {
  position: fixed; bottom: 80px; left: 50%; transform: translateX(-50%);
  background: rgba(0,0,0,0.78); color: #fff; font-size: 13px;
  padding: 10px 24px; border-radius: 22px; z-index: 999;
  white-space: nowrap; font-weight: 500;
  box-shadow: 0 4px 20px rgba(0,0,0,0.3);
}
.toast-fade-enter-active, .toast-fade-leave-active {
  transition: all 0.3s ease;
}
.toast-fade-enter, .toast-fade-leave-to {
  opacity: 0; transform: translateX(-50%) translateY(10px);
}

/* ===== 解释权声明 ===== */
.disclaimer-text {
  text-align: center;
  font-size: 12px;
  color: #999;
  padding: 10px 16px 30px;
  line-height: 1.5;
  margin-top: 10px;
}
</style>


// WEBPACK FOOTER //
// src/components/VoteRules.vue